import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useEmpresa } from "@/context/EmpresaContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, Check, X, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invitaciones() {
  const navigate = useNavigate();
  const { refreshEmpresas } = useEmpresa();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shares/pending`);
      setItems(res.data || []);
    } catch (e) {
      toast.error("Error cargando invitaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAccept = async (share) => {
    setBusy(share.id);
    try {
      await axios.post(`${API}/shares/${share.id}/accept`);
      toast.success(`Invitación aceptada a '${share.empresa_nombre || "empresa"}'`);
      await load();
      await refreshEmpresas(); // la empresa compartida debe aparecer en el selector
      navigate("/empresas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aceptar");
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async (share) => {
    setBusy(share.id);
    try {
      await axios.post(`${API}/shares/${share.id}/reject`);
      toast.success("Invitación rechazada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al rechazar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="invitaciones-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Invitaciones</h1>
        <p className="text-muted-foreground mt-1">
          Empresas a las que te han invitado a colaborar
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">Sin invitaciones</h3>
            <p className="text-muted-foreground text-center max-w-sm">
              Cuando alguien te invite a colaborar en una empresa, aparecerá aquí.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <Card key={s.id} data-testid={`invitacion-${s.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{s.empresa_nombre || "Empresa"}</CardTitle>
                      <CardDescription>
                        Permiso de{" "}
                        <Badge variant={s.permission === "write" ? "default" : "outline"} className="ml-1">
                          {s.permission === "write" ? "lectura + escritura" : "solo lectura"}
                        </Badge>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(s.created_at).toLocaleDateString("es-ES")}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(s)}
                    disabled={busy === s.id}
                    data-testid={`reject-${s.id}`}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleAccept(s)}
                    disabled={busy === s.id}
                    data-testid={`accept-${s.id}`}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Aceptar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
